import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useTranslations } from '../context/TranslationContext';
import ModalComponent from '../ModalComponent';
import Button from '../legacy/core/Button';
import AlpacaForm from '../AlpacaForm';
import fetchCustom, { callLinkAction } from '../../wp_helpers/fetchCustom';
import getResponseJson from '../../wp_helpers/getResponseJson';
import { notification } from '../../utils/utils';
import Skeleton from '@components/Job/components/Skeleton';
import Error from '@components/Job/components/Error';
import './index.less';
import classNames from 'classnames';

const ExtraQuestionsEditor = forwardRef(({ jobId, hideAddButton = false, onSaveComplete }, ref) => {
    const { t, updateTranslations } = useTranslations();
    const formRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [addButtonClicked, setAddButtonClicked] = useState(false);

    useImperativeHandle(ref, () => ({
        showModal: () => {
            setShowModal(true);
        },
        hideModal: () => {
            hideModal()
        },
    }));

    const fetchExtraQuestionsData = async () => {
        try {
            setLoading(true);
            const url = `/api/jobs/${jobId}/ui?expand=extra_questions`;
            const response = await fetchCustom(url);

            if (response.ok) {
                const { extra_questions = {} } = await getResponseJson(response);
                if (extra_questions) {
                    setData(extra_questions);
                    updateTranslations(extra_questions?.translations || {});
                    return extra_questions;
                }
            }
        } catch (err) {
            console.error('Error fetching extra questions:', err);
        } finally {
            setLoading(false);
        }
    };

    const formSchema = useMemo(() => data?.form, [data?.form]);
    const canSave = !!data?._links?.put?.url;

    useEffect(() => {
        if (jobId) {
            fetchExtraQuestionsData()
        }
    }, [jobId]);

    const hideModal = () => {
        setShowModal(false);
        setAddButtonClicked(false);
    }

    const handleSave = async () => {
        if (!formRef.current || !canSave) return;

        try {
            setSaving(true);
            const formData = formRef.current.getFormData();

            const response = await callLinkAction(data?._links, 'put', {
                body: JSON.stringify({ form: formData })
            });

            if (!response.ok) {
                throw new Error('Failed to save extra questions: ' + response.statusText);
            }
            notification(t('Extra question updated!'), { type: 'success' });
            hideModal();
            onSaveComplete?.();
            fetchExtraQuestionsData();
        } catch (error) {
            console.error('Error saving extra questions:', error);
            notification(t('Failed to save extra questions'), { type: 'error' });
            return false;
        } finally {
            setSaving(false);
        }
    };

    const renderAddButton = () => {
        if (!hideAddButton) {
            return (
                <>
                    <span>{t('Extra questions')} (<span id="number_of_extra_questions">{data?.number_of_questions || 0}</span>)</span>
                    <a className="btn btn-link application-form-extra-questions-open" onClick={() => setShowModal(true)}>
                        <span className="icon-svg-plus-circle"></span>
                        {t('Add extra questions')}
                    </a>
                </>
            );
        }
        return null;
    };

    const clickAddButton = () => {
        if (addButtonClicked) {
            return
        }
        setTimeout(() => {
            if (!document.querySelectorAll('.application-form-extra-questions .alpaca-container-item')?.length) {
                document.querySelector('.application-form-extra-questions .alpaca-array-toolbar-action')?.click();
                setAddButtonClicked(true);
            }
        }, 0)
    }

    return (
        <>
            {renderAddButton()}
            <ModalComponent
                show={showModal}
                closeModal={() => hideModal()}
                closeModalConfirmationMessage={t('Are you sure you want to close the dialogue? All changes will be lost.')}
            >
                <div className="overlay overlay-w-735">
                    <div className="overlay-header">
                        <h3>{t('Extra questions')}</h3>
                    </div>
                    <div className={classNames('overlay-main application-form-extra-questions', {
                        'hide-add-button': !addButtonClicked
                    })}>
                        <Skeleton type="form" show={loading} />
                        <Error value={!loading && !formSchema ? t('Failed to load extra questions form') : null} />
                        {!loading && formSchema && <AlpacaForm
                            ref={formRef}
                            formId="extra-questions-form"
                            formSchema={formSchema}
                            callback={clickAddButton}
                        />}
                    </div>
                    <div className="overlay-footer">
                        <div className="btn-container">
                            <Button
                                type="link"
                                onClick={() => hideModal()}
                                className="overlay-cancel"
                            >
                                {t('Close')}
                            </Button>
                            <Button
                                v2={true}
                                type="primary"
                                onClick={handleSave}
                                disabled={saving}
                                className="application-form-extra-questions-submit"
                            >
                                {saving ? t('Saving...') : t('Save')}
                            </Button>
                        </div>
                    </div>
                </div>
            </ModalComponent>
        </>
    );
});

export default ExtraQuestionsEditor;